(() => {
  const config = window.TLJOpenAIAdsConfig;
  if (!config?.pixelId) return;

  ((windowObject, documentObject, scriptTag, sourceUrl) => {
    if (windowObject.oaiq) return;
    const queue = function () {
      queue.q.push(arguments);
    };
    queue.q = [];
    windowObject.oaiq = queue;
    const script = documentObject.createElement(scriptTag);
    script.async = true;
    script.src = sourceUrl;
    const firstScript = documentObject.getElementsByTagName(scriptTag)[0];
    if (firstScript?.parentNode) {
      firstScript.parentNode.insertBefore(script, firstScript);
    } else {
      documentObject.head.appendChild(script);
    }
  })(window, document, 'script', 'https://bzrcdn.openai.com/sdk/oaiq.min.js');

  const state = {
    consentAllowed: false,
    initialEventsSent: false,
    optOut: true,
  };
  const zeroDecimalCurrencies = new Set([
    'BIF',
    'CLP',
    'DJF',
    'GNF',
    'JPY',
    'KMF',
    'KRW',
    'MGA',
    'PYG',
    'RWF',
    'UGX',
    'VND',
    'VUV',
    'XAF',
    'XOF',
    'XPF',
  ]);

  window.oaiq('consent', false);
  window.oaiq('init', { pixelId: config.pixelId, debug: false });

  const integer = (value, fallback = 0) => {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? Math.max(0, Math.round(parsed)) : fallback;
  };

  const normalizeShopifyAmount = (value, currency = config.currency) => {
    const amount = integer(value);
    return zeroDecimalCurrencies.has(String(currency).toUpperCase()) ? Math.round(amount / 100) : amount;
  };

  const normalizeAmount = (value, currency = config.currency) => {
    const parsed = Number(value);
    if (!Number.isFinite(parsed)) return 0;

    const normalizedCurrency = String(currency).toUpperCase();
    const exponent = zeroDecimalCurrencies.has(normalizedCurrency) ? 0 : 2;
    return Math.max(0, Math.round(parsed * 10 ** exponent));
  };

  const measure = (eventName, eventData) => {
    if (!state.consentAllowed || !eventName || !eventData) return;

    try {
      if (state.optOut) {
        window.oaiq('measure', eventName, eventData, { opt_out: true });
      } else {
        window.oaiq('measure', eventName, eventData);
      }
    } catch (error) {
      // Measurement must never interrupt shopping or cart actions.
    }
  };

  const sendInitialEvents = () => {
    if (state.initialEventsSent || !state.consentAllowed) return;
    state.initialEventsSent = true;

    measure('page_viewed', {
      type: 'contents',
      contents: [
        {
          id: String(config.product?.id || config.product?.variantId || config.page?.id || window.location.pathname),
          name: String(config.product?.name || config.page?.name || document.title),
          content_type: config.product ? 'product' : String(config.page?.contentType || 'page'),
        },
      ],
    });

    if (config.product) {
      measure('contents_viewed', {
        type: 'contents',
        amount: normalizeShopifyAmount(config.product.amount, config.product.currency),
        currency: config.product.currency || config.currency,
        contents: [
          {
            id: String(config.product.id || config.product.variantId),
            name: String(config.product.name),
            content_type: 'product',
            quantity: 1,
            amount: normalizeShopifyAmount(config.product.amount, config.product.currency),
            currency: config.product.currency || config.currency,
          },
        ],
      });
    }

    if (config.listing?.contents?.length) {
      measure('contents_viewed', {
        type: 'contents',
        contents: config.listing.contents.map((item) => ({
          ...item,
          amount: normalizeShopifyAmount(item.amount, item.currency),
        })),
      });
    }

    sendConfirmedLead();
  };

  const sendConfirmedLead = () => {
    const successMessage = document.querySelector('.newsletter-form__message--success');
    if (!successMessage) return;

    const storageKey = `tlj-openai-lead:${window.location.pathname}`;
    try {
      if (window.sessionStorage.getItem(storageKey) === 'sent') return;
      window.sessionStorage.setItem(storageKey, 'sent');
    } catch (error) {
      // Storage is optional; the confirmed server-rendered success boundary remains valid.
    }

    measure('lead_created', {
      type: 'customer_action',
    });
  };

  const updateCartSnapshot = (item, requestedQuantity) => {
    if (!item) return;

    const quantity = integer(requestedQuantity || item.quantity, 1) || 1;
    const unitAmount = integer(item.final_price ?? item.discounted_price ?? item.price);
    const itemId = item.sku || item.variant_id || item.id;
    const itemName = item.product_title || item.title || item.name;
    if (!itemId || !itemName) return;

    if (!config.cart) {
      config.cart = {
        amount: 0,
        currency: config.currency,
        contents: [],
      };
    }

    const itemKey = String(itemId);
    const existingItem = config.cart.contents.find((candidate) => String(candidate.id) === itemKey);
    if (existingItem) {
      existingItem.quantity = integer(existingItem.quantity) + quantity;
      existingItem.amount = unitAmount;
    } else {
      config.cart.contents.push({
        id: itemKey,
        name: String(itemName),
        content_type: 'product',
        quantity,
        amount: unitAmount,
        currency: config.currency,
      });
    }
    config.cart.amount = integer(config.cart.amount) + unitAmount * quantity;
  };

  const refreshConsent = () => {
    const privacy = window.Shopify?.customerPrivacy;
    if (!privacy) return;

    try {
      const allowed = privacy.analyticsProcessingAllowed() && privacy.marketingAllowed();
      state.optOut = privacy.saleOfDataAllowed() === false;
      state.consentAllowed = Boolean(allowed);
      window.oaiq('consent', state.consentAllowed);
      sendInitialEvents();
    } catch (error) {
      state.consentAllowed = false;
      window.oaiq('consent', false);
    }
  };

  let consentListenerAdded = false;
  const startConsentTracking = () => {
    refreshConsent();
    if (consentListenerAdded) return;
    consentListenerAdded = true;
    document.addEventListener('visitorConsentCollected', refreshConsent);
  };

  const loadCustomerPrivacy = () => {
    if (window.Shopify?.customerPrivacy) {
      startConsentTracking();
      return;
    }

    if (typeof window.Shopify?.loadFeatures !== 'function') {
      window.oaiq('consent', false);
      return;
    }

    window.Shopify.loadFeatures([{ name: 'consent-tracking-api', version: '0.1' }], (error) => {
      if (error) {
        window.oaiq('consent', false);
        return;
      }
      startConsentTracking();
    });
  };

  window.TLJOpenAIAds = {
    syncCart(cartState) {
      try {
        if (!cartState) return;
        config.cart = {
          amount: integer(cartState.total_price),
          currency: config.currency,
          contents: Array.isArray(cartState.items)
            ? cartState.items.map((item) => ({
                id: String(item.sku || item.variant_id || item.id),
                name: String(item.product_title || item.title || item.name),
                content_type: 'product',
                quantity: integer(item.quantity, 1) || 1,
                amount: integer(item.final_price ?? item.discounted_price ?? item.price),
                currency: config.currency,
              }))
            : [],
        };
      } catch (error) {
        return;
      }
    },
    trackItemAdded(item, requestedQuantity) {
      try {
        if (!item) return;

        const quantity = integer(requestedQuantity || item.quantity, 1) || 1;
        const unitAmount = normalizeShopifyAmount(item.final_price ?? item.discounted_price ?? item.price);
        const itemId = item.sku || item.variant_id || item.id;
        const itemName = item.product_title || item.title || item.name;

        if (!itemId || !itemName) return;
        updateCartSnapshot(item, quantity);

        measure('items_added', {
          type: 'contents',
          amount: unitAmount * quantity,
          currency: config.currency,
          contents: [
            {
              id: String(itemId),
              name: String(itemName),
              content_type: 'product',
              quantity,
              amount: unitAmount,
              currency: config.currency,
            },
          ],
        });
      } catch (error) {
        // Tracking helpers are deliberately isolated from the storefront flow.
      }
    },
  };

  loadCustomerPrivacy();
})();
